import React from 'react';
import ReactDOM from 'react-dom';
import {
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Bold,
  Italic,
  Strikethrough,
  Underline,
  Code,
  Highlighter,
  List,
  ListOrdered,
  ListTodo,
  TextQuote,
  User,
  Tag,
  Link2,
  Link,
  SquareKanban,
  TriangleAlert,
  Eye,
  Users,
  Calendar,
  Mail
} from 'lucide-react';
import { mentionReplacedDescription } from '../../../../javascript/components/HighlightedMentions/utils';
import { SlashCommandPopup } from './components/SlashCommandPopup';
import { CaretLinkPopover } from './components/CaretLinkPopover';
import type { SlashMenuItem, ShortcutConfig, User as ProjectUser } from './types';
import { buildCommentMentionItems } from '../RichTextComment/utils/commentMentionItems';
import {
  applyPrivateCommentSlashSelect,
  privateCommentSlashCopy
} from '../RichTextComment/utils/privateCommentSlashItem';
import type { Editor } from '@tiptap/core';
import type { SuggestionProps } from '@tiptap/suggestion';
import { priorities } from '../../views/CreateTask/create_task/right_panel/severity';
import { plainTaskMenuLabel } from './utils/taskSlashMenuLabel';
import { parseSlashDueDate, type ParsedSlashDueDate } from './utils/parseSlashDueDate';
import { shouldConsumeSlashSuggestionNavKey } from './utils/slashSuggestionSession';

const S = 14;
const HEAD = [Heading1, Heading2, Heading3, Heading4];

type SlashArgs = { editor: Editor; range: { from: number; to: number } };
type WithSearch = SlashMenuItem & {
  searchTerms: string[];
  parentTerms: string[];
};

function flattenForSearch(item: SlashMenuItem, parent?: SlashMenuItem): WithSearch[] {
  const ownTerms = [
    item.title.toLowerCase(),
    ...(item.aliases || []).map(a => a.toLowerCase())
  ];
  if (item.subItems?.length) {
    return item.subItems.flatMap(sub => flattenForSearch(sub, item));
  }
  return [
    {
      ...item,
      searchTerms: ownTerms,
      parentTerms: parent
        ? [parent.title.toLowerCase(), ...(parent.aliases || []).map(a => a.toLowerCase())]
        : []
    }
  ];
}

function matchesSlashQuery(leaf: WithSearch, q: string): boolean {
  if (leaf.searchTerms.some(term => term.includes(q))) return true;
  return leaf.parentTerms.some(term => term === q || term.startsWith(q) || q.startsWith(term));
}

function isAssignSlashQuery(q: string): boolean {
  if (q.length < 3) return false;
  return 'assign'.startsWith(q) || q.startsWith('assign');
}

function isCcSlashQuery(q: string): boolean {
  if (q.length < 2) return false;
  return (
    'cc'.startsWith(q) ||
    q.startsWith('cc') ||
    'notify'.startsWith(q) ||
    q.startsWith('notify') ||
    'watch'.startsWith(q) ||
    q.startsWith('watch')
  );
}

function uniqueUsersById(users: ProjectUser[]): ProjectUser[] {
  const seen = new Set<number>();
  return users.filter(user => {
    if (seen.has(user.id)) return false;
    seen.add(user.id);
    return true;
  });
}

function slashItemIdentity(item: SlashMenuItem): string {
  if (item.slashUserRow) {
    return `${item.slashPeopleAction ?? item.subtext ?? 'user'}-${item.slashUserRow.id}`;
  }
  if (item.slashTaskRow) {
    return `task-${item.slashTaskRow.localTaskId}`;
  }
  return `${item.group}-${item.title}-${item.subtext ?? ''}`;
}

function dedupeSlashItems(items: SlashMenuItem[]): SlashMenuItem[] {
  const seen = new Set<string>();
  return items.filter(item => {
    const id = slashItemIdentity(item);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function dueDateSlashItem(
  shortcutConfig: ShortcutConfig,
  parsed: ParsedSlashDueDate,
  title: string,
  aliases: string[]
): SlashMenuItem {
  return {
    title,
    subtext: 'Set due date',
    aliases,
    group: 'BugHerd',
    icon: <Calendar size={S} />,
    onSelect: ({ editor: e, range }: SlashArgs) => {
      del(e as Editor, range).run();
      shortcutConfig.onDueDateChange?.(parsed.iso);
    }
  };
}

function relativeDueDateItem(
  shortcutConfig: ShortcutConfig,
  queryLabel: string,
  title: string,
  aliases: string[],
  locale: string,
  now: Date
): SlashMenuItem | null {
  const parsed = parseSlashDueDate(queryLabel, { locale, now });
  if (!parsed) return null;
  return {
    ...dueDateSlashItem(shortcutConfig, parsed, title, aliases),
    subtext: parsed.label
  };
}

function buildDueDateSlashGroup(
  shortcutConfig: ShortcutConfig,
  locale: string,
  now: Date
): SlashMenuItem | null {
  if (!shortcutConfig.canEditDueDate) return null;
  const today = relativeDueDateItem(
    shortcutConfig,
    'today',
    'Today',
    ['today'],
    locale,
    now
  );
  const tomorrow = relativeDueDateItem(
    shortcutConfig,
    'tomorrow',
    'Tomorrow',
    ['tomorrow'],
    locale,
    now
  );
  const nextWeek = relativeDueDateItem(
    shortcutConfig,
    'next week',
    'Next week',
    ['next week', 'week'],
    locale,
    now
  );
  const subItems = [today, tomorrow, nextWeek].filter(Boolean) as SlashMenuItem[];
  if (subItems.length === 0) return null;
  return {
    title: 'Due date',
    subtext: 'Set a due date',
    aliases: ['due', 'deadline', 'date', 'due date'],
    group: 'BugHerd',
    icon: <Calendar size={S} />,
    onSelect: () => {},
    subItems
  };
}

function parsedDueDateSlashItem(
  shortcutConfig: ShortcutConfig,
  query: string,
  locale: string
): SlashMenuItem | null {
  if (!shortcutConfig.canEditDueDate) return null;
  const parsed = parseSlashDueDate(query, { locale });
  if (!parsed) return null;
  return dueDateSlashItem(shortcutConfig, parsed, parsed.label, [
    query.toLowerCase(),
    parsed.label.toLowerCase()
  ]);
}

const del = (ed: Editor, range: SlashArgs['range']) => ed.chain().focus().deleteRange(range);

type GetShortcutConfig = () => ShortcutConfig | null;

function matchesUserQuery(u: ProjectUser, query: string): boolean {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  const blob = [u.name, u.email, u.firstName ?? '', u.first_name ?? '', u.surname ?? '']
    .join(' ')
    .toLowerCase();
  if (blob.includes(q)) return true;
  return (
    u.name.toLowerCase().includes(q) ||
    u.email.toLowerCase().includes(q) ||
    !!(u.firstName && u.firstName.toLowerCase().includes(q)) ||
    !!(u.first_name && u.first_name.toLowerCase().includes(q)) ||
    !!(u.surname && u.surname.toLowerCase().includes(q))
  );
}

function insertUserMention(editor: Editor, range: SlashArgs['range'], user: ProjectUser) {
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent({
      type: 'bugherdMention',
      attrs: { id: String(user.id), label: user.name }
    })
    .insertContent(' ')
    .run();
}

function eligiblePeopleShortcutUsers(
  shortcutConfig: ShortcutConfig,
  alreadySelectedIds: number[]
): ProjectUser[] {
  return uniqueUsersById(shortcutConfig.assignableUsers).filter(
    u =>
      !alreadySelectedIds.includes(u.id) &&
      (shortcutConfig.currentUserId == null ||
        String(u.id) !== String(shortcutConfig.currentUserId))
  );
}

export function buildAtMentionAssignableUserItems(
  getConfig: GetShortcutConfig | ShortcutConfig | null,
  query: string
): SlashMenuItem[] {
  const shortcutConfig = typeof getConfig === 'function' ? getConfig() : getConfig;
  if (!shortcutConfig?.canEditAssignees || !shortcutConfig.assignableUsers?.length) {
    return [];
  }
  const usersToAssign = eligiblePeopleShortcutUsers(shortcutConfig, shortcutConfig.assignees);
  if (usersToAssign.length === 0) return [];

  return usersToAssign.filter(u => matchesUserQuery(u, query)).map(
    (user): SlashMenuItem => ({
      title: user.name,
      subtext: 'Assign',
      group: 'BugHerd',
      slashUserRow: user,
      slashPeopleAction: 'assign',
      onSelect: ({ editor: ed, range }: SlashArgs) => {
        const sc = typeof getConfig === 'function' ? getConfig() : shortcutConfig;
        if (!sc) return;
        insertUserMention(ed as Editor, range, user);
        sc.onAssigneesChange([...sc.assignees, user.id]);
      }
    })
  );
}

export function buildCcUserItems(
  getConfig: GetShortcutConfig | ShortcutConfig | null,
  query: string
): SlashMenuItem[] {
  const shortcutConfig = typeof getConfig === 'function' ? getConfig() : getConfig;
  if (
    !shortcutConfig?.canEditAssignees ||
    !shortcutConfig.onWatchersChange ||
    !shortcutConfig.assignableUsers?.length
  ) {
    return [];
  }
  const selected = [
    ...shortcutConfig.assignees,
    ...(shortcutConfig.watcherIds ?? [])
  ];
  const usersToCc = eligiblePeopleShortcutUsers(shortcutConfig, selected).filter(
    u => u.role !== 'guest' || !!shortcutConfig.guestVisible
  );
  if (usersToCc.length === 0) return [];

  return usersToCc.filter(u => matchesUserQuery(u, query)).map(
    (user): SlashMenuItem => ({
      title: user.name,
      subtext: 'CC',
      group: 'BugHerd',
      slashUserRow: user,
      slashPeopleAction: 'cc',
      onSelect: ({ editor: ed, range }: SlashArgs) => {
        const sc = typeof getConfig === 'function' ? getConfig() : shortcutConfig;
        if (!sc?.onWatchersChange) return;
        insertUserMention(ed as Editor, range, user);
        sc.onWatchersChange([...(sc.watcherIds ?? []), user.id]);
      }
    })
  );
}

function slashRow(
  group: string,
  title: string,
  subtext: string,
  aliases: string[],
  icon: React.ReactNode,
  apply: (ch: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>,
  shortcut?: string[]
): SlashMenuItem {
  return {
    title,
    subtext,
    shortcut,
    aliases,
    group,
    icon,
    onSelect: ({ editor: e, range }: SlashArgs) => {
      apply(del(e as Editor, range)).run();
    }
  };
}

type OpenLinkPopover = (editor: Editor, pos: number) => void;

function buildItems(
  getConfig: GetShortcutConfig | ShortcutConfig | null,
  query: string,
  _editor: Editor,
  openLinkPopover: OpenLinkPopover,
  locale = typeof navigator !== 'undefined' ? navigator.language : 'en-AU'
): SlashMenuItem[] {
  const shortcutConfig = typeof getConfig === 'function' ? getConfig() : getConfig;
  const q = query.toLowerCase().trim();

  const headingGroup: SlashMenuItem = {
    title: 'Heading',
    subtext: 'Section heading',
    aliases: ['h', 'heading'],
    group: 'Formatting',
    icon: <Heading1 size={S} />,
    onSelect: () => {},
    subItems: ([1, 2, 3, 4] as const).map(level => ({
      title: `Heading ${level}`,
      subtext: (['Large', 'Medium', 'Small', 'Smallest'] as const)[level - 1],
      shortcut: ['mod', 'alt', String(level)],
      aliases: [`h${level}`],
      group: 'Formatting',
      icon: React.createElement(HEAD[level - 1] as React.ElementType<{ size: number }>, { size: S }),
      onSelect: ({ editor: e, range }: SlashArgs) => {
        del(e as Editor, range).setHeading({ level }).run();
      }
    }))
  };

  const textStylingGroup: SlashMenuItem = {
    title: 'Text styling',
    subtext: 'Bold, italic, underline',
    aliases: ['bold', 'italic', 'strike', 'underline', 'highlight', 'format'],
    group: 'Formatting',
    icon: <Bold size={S} />,
    onSelect: () => {},
    subItems: [
      slashRow('Formatting', 'Bold', 'Make text bold', ['b', 'strong'], <Bold size={S} />, c => c.toggleBold(), ['mod', 'B']),
      slashRow('Formatting', 'Italic', 'Make text italic', ['i', 'em'], <Italic size={S} />, c => c.toggleItalic(), ['mod', 'I']),
      slashRow('Formatting', 'Strikethrough', 'Cross out', ['s', 'strike'], <Strikethrough size={S} />, c => c.toggleStrike(), ['mod', 'shift', 'S']),
      slashRow('Formatting', 'Underline', 'Underline text', ['u'], <Underline size={S} />, c => c.toggleUnderline(), ['mod', 'U']),
      slashRow('Formatting', 'Highlight', 'Highlight text', ['mark'], <Highlighter size={S} />, c => c.toggleHighlight(), ['mod', 'shift', 'H'])
    ]
  };

  const codeGroup: SlashMenuItem = {
    title: 'Code',
    subtext: 'Inline or block',
    aliases: ['code', '`', 'pre', '```'],
    group: 'Formatting',
    icon: <Code size={S} />,
    onSelect: () => {},
    subItems: [
      slashRow('Formatting', 'Inline code', 'Code snippet', ['`'], <Code size={S} />, c => c.toggleCode(), ['mod', 'E']),
      slashRow('Formatting', 'Code block', 'Preformatted block', ['```', 'pre'], <Code size={S} />, c => c.toggleCodeBlock(), ['mod', 'alt', 'C'])
    ]
  };

  const listGroup: SlashMenuItem = {
    title: 'List',
    subtext: 'Bullet, numbered, task',
    aliases: ['ul', 'ol', 'list', 'bullet', 'numbered', 'todo', 'task', 'checkbox'],
    group: 'Lists',
    icon: <List size={S} />,
    onSelect: () => {},
    subItems: [
      slashRow('Lists', 'Bullet list', 'Bulleted list', ['ul', 'bullet'], <List size={S} />, c => c.toggleBulletList(), ['mod', 'shift', '8']),
      slashRow('Lists', 'Numbered list', 'Ordered list', ['ol', 'ordered'], <ListOrdered size={S} />, c => c.toggleOrderedList(), ['mod', 'shift', '7']),
      slashRow('Lists', 'Todo list', 'Task list with checkboxes', ['task', 'checkbox', 'todo'], <ListTodo size={S} />, c => c.toggleTaskList(), ['mod', 'shift', '9'])
    ]
  };

  const linkItem: SlashMenuItem = {
    title: 'Link',
    subtext: 'Insert a link',
    shortcut: ['mod', 'K'],
    aliases: ['a', 'url'],
    group: 'Formatting',
    icon: <Link size={S} />,
    onSelect: ({ editor: e, range }: SlashArgs) => {
      const ed = e as Editor;
      del(ed, range).run();
      openLinkPopover(ed, range.from);
    }
  };

  const quoteItem: SlashMenuItem = {
    title: 'Block quote',
    subtext: 'Insert a blockquote',
    shortcut: ['mod', 'shift', 'B'],
    aliases: ['blockquote', 'quote'],
    group: 'Blocks',
    icon: <TextQuote size={S} />,
    onSelect: ({ editor: e, range }: SlashArgs) => {
      del(e as Editor, range).toggleBlockquote().run();
    }
  };

  const allGroups: SlashMenuItem[] = [quoteItem, codeGroup, headingGroup, linkItem, listGroup, textStylingGroup];

  const commentSlash = shortcutConfig?.commentSlash;
  if (commentSlash?.mentionableUsers?.length) {
    const mentionItems = buildCommentMentionItems(commentSlash.mentionableUsers, '');
    if (mentionItems.length > 0) {
      allGroups.unshift({
        title: 'Mention',
        subtext: 'Notify a project user',
        aliases: ['mention', '@', 'user', 'member', 'notify'],
        group: 'People',
        icon: <User size={S} />,
        onSelect: () => {},
        subItems: mentionItems
      });
    }
  }

  if (commentSlash?.canTogglePrivate) {
    const copy = privateCommentSlashCopy(commentSlash.isPrivate);
    allGroups.unshift({
      title: copy.title,
      subtext: copy.subtext,
      aliases: copy.aliases,
      group: 'BugHerd',
      icon: commentSlash.isPrivate ? <Users size={S} /> : <Eye size={S} />,
      onSelect: ({ editor: e, range }: SlashArgs) => {
        del(e as Editor, range).run();
        applyPrivateCommentSlashSelect({
          isPrivate: commentSlash.isPrivate,
          canCreatePrivate: commentSlash.canCreatePrivate,
          hasInvalidMentions: commentSlash.hasInvalidMentions,
          onToggle: commentSlash.onPrivateToggle,
          onUpgrade: commentSlash.onPrivateUpgrade
        });
      }
    });
  }

  if (shortcutConfig?.mentionableTasks?.length) {
    allGroups.splice(allGroups.length - 2, 0, {
      title: 'Link task',
      subtext: 'Reference another task',
      aliases: ['task', '##', 'ref', 'link'],
      group: 'BugHerd',
      icon: <Link2 size={S} />,
      onSelect: () => {},
      subItems: shortcutConfig.mentionableTasks.map(task => {
        const labelPlain = plainTaskMenuLabel(task);
        return {
          title: task.display,
          subtext: 'Reference this task in the description',
          aliases: [
            task.display.toLowerCase(),
            labelPlain.toLowerCase(),
            String(task.localTaskId),
            mentionReplacedDescription(task.display).toLowerCase()
          ],
          group: 'BugHerd',
          slashTaskRow: { severity: task.severity ?? 0, localTaskId: task.localTaskId, labelPlain },
          onSelect: ({ editor: ed, range }: SlashArgs) => {
            (ed as Editor)
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent({
                type: 'bugherdMention',
                attrs: { id: String(task.id), label: `##${task.localTaskId}` }
              })
              .insertContent(' ')
              .run();
          }
        };
      })
    });
  }

  if (shortcutConfig?.canEditAssignees && shortcutConfig.assignableUsers?.length) {
    const assignItems = buildAtMentionAssignableUserItems(getConfig, '').map(item => {
      const user = item.slashUserRow as ProjectUser;
      return {
        ...item,
        aliases: [user.name.toLowerCase(), user.firstName?.toLowerCase(), user.first_name?.toLowerCase()].filter(
          Boolean
        ) as string[]
      };
    });
    if (assignItems.length > 0) {
      allGroups.unshift({
        title: 'Assign user',
        subtext: 'Assign task to a team member',
        aliases: ['assign', '@', 'user'],
        group: 'BugHerd',
        icon: <User size={S} />,
        onSelect: () => {},
        subItems: assignItems
      });
    }
  }

  if (shortcutConfig?.canEditAssignees && shortcutConfig.onWatchersChange) {
    const ccItems = buildCcUserItems(getConfig, '').map(item => {
      const user = item.slashUserRow as ProjectUser;
      return {
        ...item,
        aliases: [user.name.toLowerCase(), user.firstName?.toLowerCase(), user.first_name?.toLowerCase()].filter(
          Boolean
        ) as string[]
      };
    });
    if (ccItems.length > 0) {
      allGroups.unshift({
        title: 'CC user',
        subtext: 'Notify without assigning',
        aliases: ['cc', 'notify', 'watch'],
        group: 'BugHerd',
        icon: <Mail size={S} />,
        onSelect: () => {},
        subItems: ccItems
      });
    }
  }

  if (shortcutConfig?.canEditSeverity) {
    allGroups.splice(allGroups.length - 1, 0, {
      title: 'Severity',
      subtext: 'Set task priority',
      aliases: ['severity', 'priority', '!'],
      group: 'BugHerd',
      icon: <TriangleAlert size={S} />,
      onSelect: () => {},
      subItems: priorities.map(p => ({
        title: p.name.replace(/\b\w/g, l => l.toUpperCase()),
        subtext: 'Set severity',
        aliases: [p.name.toLowerCase()],
        group: 'BugHerd',
        slashSeverityRow: { priority: p.id ?? 0 },
        onSelect: ({ editor: e, range }: SlashArgs) => {
          del(e as Editor, range).run();
          shortcutConfig!.onSeverityChange(p.id);
        }
      }))
    });
  }

  if (shortcutConfig?.canEditStatus) {
    const statusColumns = shortcutConfig.columns.filter(c => c.name !== 'feedback');
    if (statusColumns.length > 0) {
      allGroups.splice(allGroups.length - 1, 0, {
        title: 'Status',
        subtext: 'Set task status',
        aliases: ['status', '$'],
        group: 'BugHerd',
        icon: <SquareKanban size={S} />,
        onSelect: () => {},
        subItems: statusColumns.map(col => ({
          title: col.name,
          subtext: 'Set status',
          aliases: [col.name.toLowerCase()],
          group: 'BugHerd',
          slashStatusRow: true,
          onSelect: ({ editor: e, range }: SlashArgs) => {
            del(e as Editor, range).run();
            shortcutConfig!.onStatusChange(col.id);
          }
        }))
      });
    }
  }

  if (shortcutConfig?.canEditTags) {
    const tagsToAdd = shortcutConfig.availableTags.filter(
      tag => !shortcutConfig.tags.find(t => t.id === tag.id)
    );
    if (tagsToAdd.length > 0) {
      allGroups.unshift({
        title: 'Add tag',
        subtext: 'Add a tag to the task',
        aliases: ['tag', '#'],
        group: 'BugHerd',
        icon: <Tag size={S} />,
        onSelect: () => {},
        subItems: tagsToAdd.map(tag => ({
          title: tag.display,
          subtext: 'Add tag',
          aliases: [tag.display.toLowerCase()],
          group: 'BugHerd',
          icon: <Tag size={S} />,
          onSelect: ({ editor: e, range }: SlashArgs) => {
            del(e as Editor, range).run();
            shortcutConfig!.onTagsChange([...shortcutConfig!.tags, tag]);
          }
        }))
      });
    }
  }

  const dueDateGroup =
    shortcutConfig && buildDueDateSlashGroup(shortcutConfig, locale, new Date());
  if (dueDateGroup) {
    allGroups.unshift(dueDateGroup);
  }

  const parsedDue =
    shortcutConfig && parsedDueDateSlashItem(shortcutConfig, query, locale);

  if (q) {
    const flat = allGroups.flatMap(g => flattenForSearch(g));
    const matched = flat.filter(leaf => matchesSlashQuery(leaf, q)).filter(leaf => {
      if (isAssignSlashQuery(q) && !isCcSlashQuery(q) && leaf.slashPeopleAction === 'cc') {
        return false;
      }
      if (isCcSlashQuery(q) && !isAssignSlashQuery(q) && leaf.slashPeopleAction === 'assign') {
        return false;
      }
      return true;
    });
    const items = dedupeSlashItems(matched.map(({ searchTerms: _s, parentTerms: _p, ...item }) => item));
    if (parsedDue && !items.some(item => item.title === parsedDue.title)) {
      return [parsedDue, ...items];
    }
    return items;
  }

  return allGroups;
}

export { buildItems };

export type SlashCommandConfig = {
  items: (props: { query: string; editor: Editor }) => SlashMenuItem[];
  render: () => {
    onStart: (props: SuggestionProps<SlashMenuItem, SlashMenuItem>) => void;
    onUpdate: (props: SuggestionProps<SlashMenuItem, SlashMenuItem>) => void;
    onKeyDown: (props: { event: KeyboardEvent }) => boolean;
    onExit: () => void;
  };
};

export function usePopupSuggestionRender(
  popupContainerRef: React.RefObject<HTMLDivElement | null>
) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const keyHandlerRef = React.useRef<((e: KeyboardEvent) => void) | null>(null);
  const dismissedRef = React.useRef(false);

  return React.useCallback(() => {
    const unmountPopup = () => {
      if (containerRef.current) ReactDOM.unmountComponentAtNode(containerRef.current);
    };

    const mount = (p: SuggestionProps<SlashMenuItem, SlashMenuItem>) => {
      if (!containerRef.current) return;
      if (dismissedRef.current) {
        unmountPopup();
        return;
      }
      ReactDOM.render(
        <SlashCommandPopup
          items={p.items}
          command={p.command}
          editor={p.editor}
          range={p.range}
          query={p.query}
          decorationNode={p.decorationNode}
          clientRect={p.clientRect}
          containerRef={popupContainerRef}
          keyHandlerRef={keyHandlerRef}
          dismissSuggestion={() => {
            dismissedRef.current = true;
            unmountPopup();
          }}
        />,
        containerRef.current
      );
    };
    return {
      onStart: (p: SuggestionProps<SlashMenuItem, SlashMenuItem>) => {
        const container = popupContainerRef?.current ?? document.body;
        if (!containerRef.current) {
          containerRef.current = document.createElement('div');
          container.appendChild(containerRef.current);
        }
        mount(p);
      },
      onUpdate: mount,
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (
          !shouldConsumeSlashSuggestionNavKey({
            dismissed: dismissedRef.current,
            key: event.key
          })
        ) {
          return false;
        }
        keyHandlerRef.current?.(event);
        return true;
      },
      onExit: () => {
        dismissedRef.current = false;
        unmountPopup();
      }
    };
  }, [popupContainerRef]);
}

function useCaretLinkPopover(
  popupContainerRef: React.RefObject<HTMLDivElement | null>
): OpenLinkPopover {
  const mountRef = React.useRef<HTMLDivElement | null>(null);

  const close = React.useCallback(() => {
    const mount = mountRef.current;
    if (!mount) return;
    ReactDOM.unmountComponentAtNode(mount);
    mount.remove();
    mountRef.current = null;
  }, []);

  React.useEffect(() => close, [close]);

  return React.useCallback(
    (editor: Editor, pos: number) => {
      close();
      const container = popupContainerRef?.current ?? document.body;
      const mount = document.createElement('div');
      container.appendChild(mount);
      mountRef.current = mount;
      ReactDOM.render(
        <CaretLinkPopover
          editor={editor}
          pos={pos}
          containerRef={popupContainerRef}
          onClose={close}
        />,
        mount
      );
    },
    [popupContainerRef, close]
  );
}

export function useSlashCommandConfig(
  shortcutConfig: ShortcutConfig | null,
  popupContainerRef: React.RefObject<HTMLDivElement | null>
): SlashCommandConfig {
  const shortcutConfigRef = React.useRef(shortcutConfig);
  shortcutConfigRef.current = shortcutConfig;
  const openLinkPopover = useCaretLinkPopover(popupContainerRef);
  const items = React.useCallback(
    ({ query, editor }: { query: string; editor: Editor }) =>
      buildItems(() => shortcutConfigRef.current, query, editor, openLinkPopover),
    [openLinkPopover]
  );
  const render = usePopupSuggestionRender(popupContainerRef);

  return { items, render };
}

export function useAtMentionCommandConfig(
  shortcutConfig: ShortcutConfig | null,
  popupContainerRef: React.RefObject<HTMLDivElement | null>
): Pick<SlashCommandConfig, 'items' | 'render'> {
  const shortcutConfigRef = React.useRef(shortcutConfig);
  shortcutConfigRef.current = shortcutConfig;
  const items = React.useCallback(
    ({ query }: { query: string; editor: Editor }) =>
      buildAtMentionAssignableUserItems(() => shortcutConfigRef.current, query),
    []
  );
  const render = usePopupSuggestionRender(popupContainerRef);

  return { items, render };
}
